/**
 * Level *data model*.
 *
 * This module intentionally contains no three.js and no game logic - it only
 * describes what a level is made of, plus a few authoring helpers. The concrete
 * blockout lives in `src/config/level.ts`, and `src/world/World.ts` turns a
 * definition into meshes + colliders.
 *
 * When the real LAN venue is modelled in Blender, the plan is to keep this exact
 * shape: visuals come from a GLB, while `solids` / `doors` / `checkpoints` stay
 * hand-authored (or are exported from named empties in the .blend file).
 */

import type { Vec3 } from '../utils/math';
import type { Box } from './levelBox';
import type { DecorSpec, FloorSpec, SolidSpec } from './levelSolids';
import type {
  CylinderSpec,
  CampTentSpec,
  DiscoSpec,
  DoorSpec,
  HandleSpec,
  HoseSpec,
  ProjectionSpec,
  SaunaSpec,
  ScreenSpec,
  SignSpec,
  SmokeSpec,
} from './levelProps';
import type {
  SitterSpec,
  SleeperSpec,
  SprawlerSpec,
  StanderSpec,
} from './levelPeople';
import type {
  AamupalaSpec,
  CheckpointSpec,
  FinishSpec,
  HotTubSpec,
  IkkunaSpec,
  KamiinaSpec,
  MuurikkaSpec,
  SimaSpec,
} from './levelStations';

export * from './levelBox';
export * from './levelSolids';
export * from './levelProps';
export * from './levelPeople';
export * from './levelStations';
export * from './levelAuthoring';

export interface LevelDefinition {
  name: string;
  spawn: { position: Vec3; yaw: number };
  bounds: Box;
  floors: FloorSpec[];
  solids: SolidSpec[];
  decor: DecorSpec[];
  cylinders: CylinderSpec[];
  smoke: SmokeSpec[];
  doors: DoorSpec[];
  /**
   * The painikkeet on the leaves that never open - the ones hung on a solid
   * wall as dressing. The doors above carry their own; see `HandleSpec`.
   *
   * Dressing like `decor`, and outside the fingerprint for the same reason:
   * nothing in the level is any harder or easier to get past for having a
   * handle drawn on it.
   */
  handles: HandleSpec[];
  sleepers: SleeperSpec[];
  /**
   * Somebody asleep who is *not* in `sleepers`, because he is not there to be
   * run into: the player himself, drawn in his own bed for as long as the run
   * has not started. Rendering-only, so he is outside the fingerprint - see
   * `simulationRelevantLevel` in src/sim/fingerprint.ts.
   */
  sleepingPlayer?: SleeperSpec;
  /**
   * The ones who are awake - which is one man on a bench in the corner of the
   * juhlasali. Nothing about a sitter is solid, so like `decor` and `signs` he
   * is outside the fingerprint. See `SitterSpec`.
   */
  sitters: SitterSpec[];
  /**
   * And the ones who are neither: face down on the floor where they stopped.
   * Appearance is outside the fingerprint; the tent audience also has
   * matching collision boxes in `solids`. See `SprawlerSpec`.
   */
  sprawlers: SprawlerSpec[];
  /**
   * And the ones on their feet who came with nothing attached - the cook does
   * not appear here, because he is part of the pan he stands behind. Dressing
   * like the three above, and outside the fingerprint. See `StanderSpec`.
   */
  standers: StanderSpec[];
  /**
   * The camp on the field east of the juhlanavetta: everybody who slept in
   * their own tent rather than under a table. Solid footprints are shared
   * by simulation, replay verification and the collider overlay.
   */
  campTents: CampTentSpec[];
  /**
   * Breakfast in the yard: the muurikka on the kivetys and the man at it.
   * Dressing like the two above, and outside the fingerprint for the same
   * reason - see `MuurikkaSpec`.
   */
  muurikka?: MuurikkaSpec;
  /**
   * And the part of that set-piece that is not dressing: the place Kimmo has
   * to stand to be fed, and the man who will not let him past the porstua
   * until he has been. Hashed, unlike the pan itself - see `AamupalaSpec`.
   */
  aamupala?: AamupalaSpec;
  /**
   * And the same bargain one room further on: the empty tuoppi on the table,
   * the two hanat that fill it, and the crowd that will not let anybody at
   * his own chair until it is full. Hashed apart from the people - see
   * `SimaSpec`.
   */
  sima?: SimaSpec;
  /**
   * And the one thing in the hall that is neither: a door you may try and that
   * is never going to open. Dressing with a prompt on it, and outside the
   * fingerprint - see `SaunaSpec`.
   */
  sauna?: SaunaSpec;
  signs: SignSpec[];
  screens: ScreenSpec[];
  hoses: HoseSpec[];
  projection?: ProjectionSpec;
  /**
   * The rig in the XLMOTO tent: a mirror ball, the strobes and the lasers.
   * Dressing, like the smoke and the projector - see `DiscoSpec`.
   */
  disco?: DiscoSpec;
  checkpoints: CheckpointSpec[];
  finish: FinishSpec;
  hotTub: HotTubSpec;
  kamiina: KamiinaSpec;
  /**
   * Name of the solid that carries the venue's power: the bemari's charging
   * cable, strung across the yard from the tolppa the juhlanavetta is fed
   * from. Kick it and the lights go out - see `RunSimulation.powerIsOut`.
   *
   * A name rather than the collider itself, because the simulation must not
   * have to know which of a level's solids is a cable. It is not in
   * `simulationRelevantLevel`: the blackout changes what you can *see*, never
   * where anything is, so it costs no leaderboard time.
   */
  powerCable: string;
  /** The glass in the pirtti's south window. See `IkkunaSpec`. */
  ikkuna: IkkunaSpec;
}

/**
 * The juhlanavetta's own footprint in world XZ, from the outer faces of its
 * four walls.
 *
 * Derived rather than authored, so it follows the building if a wall moves.
 * Inside this box is inside: the eteinen and the WCs are in the building, the
 * porch out front is not - which is exactly the edge that matters, because the
 * porch is the one place you can stand and see both.
 */
export function venueFootprint(level: LevelDefinition): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  const walls = level.solids.filter((solid) => solid.name.startsWith('navetta-'));
  return walls.reduce(
    (box, wall) => ({
      minX: Math.min(box.minX, wall.box.minX),
      maxX: Math.max(box.maxX, wall.box.maxX),
      minZ: Math.min(box.minZ, wall.box.minZ),
      maxZ: Math.max(box.maxZ, wall.box.maxZ),
    }),
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity },
  );
}

